import { normalizeStatus, PUBLISH_STATUS_LABELS } from '@/lib/publishStatus';
import { resolveValue } from '@/lib/multilingual';
import { dayMonthYear } from '@/lib/serverTime';

/*
 * How a passport's changelog entries read: what kind of entry each is, and
 * its values as text. Shared by the feed (ChangelogFeed) and the editor's CSV
 * export.
 */

export function changelogAction(event) {
    return event.old_value ? 'changed' : 'created';
}

// Publish status changelog entries use the reserved property key
// 'publish_status' instead of an ontology property id.
export function isPublishStatusEvent(event) {
    return event.property === 'publish_status';
}

/**
 * The latest release, read off the changelog's status entries (oldest first,
 * as the passport read embeds them), or undefined when none is loaded. It is
 * the entry that made the release: a re-release with no changes returns to
 * the release it was revising, which keeps its number and its date.
 */
export function latestRelease(changelog) {
    const released = (changelog ?? []).filter((event) => isPublishStatusEvent(event) && event.new_value === 'released');
    const latest = released.at(-1);
    const number = Number(latest?.release_number ?? 0);

    return number ? released.find((event) => Number(event.release_number ?? 0) === number) : latest;
}

/**
 * For each publish-status entry of `events` (newest first, as the feed shows
 * them), the release number the passport was at before it: the number on the
 * nearest older status entry that carries one, or null when none is loaded.
 * What tells a re-release with no changes from a new release (see
 * publishStatusLabel).
 */
export function previousReleaseNumbers(events) {
    const previous = new Map();
    let older = null;
    for (const event of [...(events ?? [])].reverse()) {
        if (!isPublishStatusEvent(event)) {
            continue;
        }
        previous.set(event, older);
        if (event.release_number != null) {
            older = Number(event.release_number);
        }
    }

    return previous;
}

/**
 * A publish-status changelog entry, named — for the feed and the CSV export.
 *
 * A status entry carries the passport's release number after the move
 * (`release_number`), so:
 *
 * - `→ released` is "Released as #n"; from revising, at the number the
 *   passport was already at (`previousReleaseNumber`, see
 *   previousReleaseNumbers), nothing had changed and it is "Released again as
 *   #n (no changes)";
 * - `released → revising` is "Revising (release #n unlocked)";
 * - `→ archived` is "Archived";
 * - `→ draft` is "Draft", and "Draft (was release #n)" for a release
 *   cancelled back to draft (history from before revising existed);
 * - an absent status reads as draft, as legacy history has it.
 */
export function publishStatusLabel(entry, previousReleaseNumber = null) {
    const status = normalizeStatus(entry?.new_value);
    const number = Number(entry?.release_number ?? 0);
    const label = PUBLISH_STATUS_LABELS[status] ?? status;

    if (!number) {
        return label;
    }
    if (status === 'released') {
        return entry.old_value === 'revising' && Number(previousReleaseNumber ?? 0) === number
            ? `Released again as #${number} (no changes)`
            : `Released as #${number}`;
    }
    if (status === 'revising') {
        return `Revising (release #${number} unlocked)`;
    }

    return status === 'draft' ? `${label} (was release #${number})` : label;
}

/** A changelog value as text; `dayFirst` writes a date day first ("11 Sep 2026") instead of in the browser's locale. */
export function changelogValue(eventValue, propertyType = null, { dayFirst = false } = {}) {
    switch (propertyType) {
        case 'short_text':
        case 'long_text':
        case 'number':
        case 'measurement':
            return typeof eventValue === 'object' && eventValue !== null ? JSON.stringify(eventValue) : eventValue;
        case 'boolean':
            return eventValue ? 'true' : 'false';
        case 'date':
            return (dayFirst && dayMonthYear(eventValue)) || new Date(eventValue).toLocaleDateString();
        case 'file':
        case 'image':
            return eventValue?.filename || 'Unknown file';
        default:
            return typeof eventValue === 'string' ? eventValue : JSON.stringify(eventValue);
    }
}

/** A changelog value as the feed shows it: one language, with its unit (`dayFirst`: see changelogValue). */
export function displayValue(value, property, defaultLanguage, { dayFirst = false } = {}) {
    let shown = value;
    if (shown && typeof shown === 'object' && !Array.isArray(shown) && !['file', 'image', 'range'].includes(property?.type)) {
        shown = resolveValue(shown, null, defaultLanguage).value;
    }
    if (Array.isArray(shown)) {
        return shown.join(', ');
    }
    const text = changelogValue(shown, property?.type, { dayFirst });
    const unit = property?.type === 'measurement' ? property?.value?.measurement : null;

    return unit ? `${text} ${unit}` : String(text ?? '');
}
